import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { getFirebaseToken } from '../../utils/getFirebaseToken';
import { isLoneWolfFormOfType, getLoneWolfSigningStatus } from '../../utils/loneWolfForms';
import { filterParticipantsForSignature, extractParticipantsList } from '../../utils/signatureParticipants';
import './TransactionDocuments.css';
import './LoneWolfStageFormCard.css';

/**
 * The TDS or SPQ form already attached to this transaction's Lone Wolf form package (the same
 * row the Documents → Forms tab shows), surfaced inside its "Create / Upload Signed TDS/SPQ"
 * prospect stage. Clicking the row views the wizard-filled form; the kebab offers View and
 * Send For Signature — the same filled-preview and filled send-for-signature endpoints the
 * Forms tab uses for TDS/SPQ. Renders nothing if the form isn't attached or can't be loaded,
 * so the stage card itself is never affected.
 *
 * @param formType 'TDS' | 'SPQ'
 */
const LoneWolfStageFormCard = ({ transaction, formType, participants = [], userRole = '' }) => {
  const [form, setForm] = useState(null);
  const [signing, setSigning] = useState(null);

  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);

  const [isViewing, setIsViewing] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureParticipants, setSignatureParticipants] = useState([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState(new Set());
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const transactionId = transaction?.id;

  const loadForm = useCallback(async () => {
    if (!transactionId) return;
    try {
      const firebaseToken = await getFirebaseToken();
      const authHeaders = { headers: { 'Authorization': `Bearer ${firebaseToken}` } };
      const formsResp = await apiClient.get(API_ENDPOINTS.lonewolfTransactionForms(transactionId), authHeaders);
      const forms = Array.isArray(formsResp.data) ? formsResp.data : [];
      // strikeOut:true means voided — same exclusion as the Forms tab.
      const match = forms.find((f) => !f.strikeOut && isLoneWolfFormOfType(f, formType)) || null;
      setForm(match);
      if (!match) return;

      // Signing status is best-effort, same as the Forms tab — no status just means "Draft".
      try {
        const signingResp = await apiClient.get(API_ENDPOINTS.lonewolfSigningDocuments(transactionId), authHeaders);
        const signings = Array.isArray(signingResp.data) ? signingResp.data : [];
        setSigning(signings.find((s) => s.formVersionId && s.formVersionId === match.versionId) || null);
      } catch (e) {
        console.warn(`[LW Stage ${formType}] Failed to fetch signing status:`, e);
      }
    } catch (e) {
      console.warn(`[LW Stage ${formType}] Failed to load Lone Wolf forms:`, e);
      setForm(null);
    }
  }, [transactionId, formType]);

  useEffect(() => { loadForm(); }, [loadForm]);

  // Close the kebab menu on any outside click.
  useEffect(() => {
    if (!menuPos) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuPos(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuPos]);

  useEffect(() => () => { if (pdfBlobUrl) window.URL.revokeObjectURL(pdfBlobUrl); }, [pdfBlobUrl]);

  if (!form) return null;

  const formName = form.name || form.formName || `${formType} Form`;
  const status = getLoneWolfSigningStatus(signing);

  const handleView = async (e) => {
    if (e) e.stopPropagation();
    setMenuPos(null);
    if (isViewing) return;
    if (!form.versionId) {
      alert('Form version is missing');
      return;
    }
    setIsViewing(true);
    try {
      const firebaseToken = await getFirebaseToken();
      // Filled preview: the TDS/SPQ comes back populated from the saved wizard.
      const response = await apiClient.get(API_ENDPOINTS.lonewolfFilledPreview(transactionId, form.versionId), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfBlobUrl(window.URL.createObjectURL(blob));
    } catch (error) {
      console.error(`Error opening Lone Wolf ${formType} form:`, error);
      alert(error.response?.data?.error || 'Failed to open form. Please try again.');
    } finally {
      setIsViewing(false);
    }
  };

  const handleClosePdfViewer = () => {
    if (pdfBlobUrl) window.URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
  };

  const handleOpenSendForSignature = async (e) => {
    if (e) e.stopPropagation();
    setMenuPos(null);
    setSendError(null);
    setSelectedParticipantIds(new Set()); // none pre-selected — user picks recipients manually
    const initialParticipants = filterParticipantsForSignature(participants, userRole);
    setSignatureParticipants(initialParticipants);
    setIsLoadingParticipants(initialParticipants.length === 0);
    setShowSignatureModal(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.transactionParticipants(transactionId), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const fresh = filterParticipantsForSignature(extractParticipantsList(response.data), userRole);
      if (fresh.length > 0) setSignatureParticipants(fresh);
    } catch (error) {
      console.error('Error fetching participants for signature:', error);
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  const handleToggleParticipant = (key) => {
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleCloseSignatureModal = () => {
    if (isSending) return;
    setShowSignatureModal(false);
  };

  // Same endpoint as the Forms tab's TDS/SPQ send: the backend fills the blank from the
  // completed wizard and sends THOSE bytes; recipients are re-validated server-side against
  // the transaction's Lone Wolf contacts.
  const handleSend = async () => {
    const emails = signatureParticipants
      .filter((p) => p.email && p.email.includes('@') && selectedParticipantIds.has(p.id || p.email))
      .map((p) => p.email);
    if (emails.length === 0) return;
    setIsSending(true);
    setSendError(null);
    try {
      const firebaseToken = await getFirebaseToken();
      const redirectUrl = `${window.location.origin}/lonewolf-signing-callback?txId=${transactionId}&versionId=${form.versionId}`;
      const response = await apiClient.post(
        API_ENDPOINTS.lonewolfSendFormForSignature(transactionId, form.versionId),
        emails,
        { headers: { 'Authorization': `Bearer ${firebaseToken}` }, params: { redirectUrl } }
      );
      const ssoUrl = response.data?.ssoUrl;
      if (!ssoUrl) {
        setSendError('Lone Wolf did not return a signing link.');
        return;
      }
      setShowSignatureModal(false);
      window.open(ssoUrl, '_blank', 'noopener,noreferrer');
      await loadForm();
    } catch (error) {
      console.error(`Error sending ${formType} form for signature via Lone Wolf:`, error);
      setSendError(error.response?.data?.error || 'Failed to send form for signature.');
    } finally {
      setIsSending(false);
    }
  };

  const openMenu = (e) => {
    e.stopPropagation();
    if (menuPos) { setMenuPos(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    // Fixed-position menu, right-aligned under the kebab (min-width 190px in .txn-doc-kebab-menu).
    setMenuPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 190) });
  };

  return (
    <div className="lw-stage-form">
      <div className="txn-rpa-section-label">{formType} FORM</div>
      <div
        className="lw-stage-form-row"
        role="button"
        tabIndex={0}
        onClick={handleView}
        onKeyDown={(e) => { if (e.key === 'Enter') handleView(e); }}
      >
        <span className="lw-stage-form-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        </span>
        <div className="lw-stage-form-info">
          <div className="lw-stage-form-name">{formName}</div>
          <div className="lw-stage-form-sub">{isViewing ? 'Loading…' : 'Lone Wolf Form'}</div>
        </div>
        <div className="txn-doc-status" style={status.style}>{status.label}</div>
        <div className="txn-doc-kebab-wrap" ref={menuRef}>
          <button
            type="button"
            className="txn-doc-kebab-btn"
            aria-label={`${formType} form actions`}
            onClick={openMenu}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
          </button>
          {menuPos && (
            <div
              className="txn-doc-kebab-menu"
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="txn-doc-kebab-item" onClick={handleView}>View</button>
              <button type="button" className="txn-doc-kebab-item" onClick={handleOpenSendForSignature}>Send For Signature</button>
            </div>
          )}
        </div>
      </div>

      {pdfBlobUrl && createPortal(
        <div className="pdf-viewer-overlay" onClick={handleClosePdfViewer}>
          <div className="pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <div className="pdf-viewer-title-section">
                <span className="pdf-viewer-icon">📄</span>
                <h3 className="pdf-viewer-title">{formName}</h3>
              </div>
              <div className="pdf-viewer-actions">
                <a href={pdfBlobUrl} download={formName} className="pdf-viewer-download-btn" title="Download">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </a>
                <button className="pdf-viewer-close-btn" onClick={handleClosePdfViewer} title="Close">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="pdf-viewer-body">
              <iframe src={pdfBlobUrl} title={formName} className="pdf-viewer-iframe" />
            </div>
          </div>
        </div>,
        document.body
      )}

      {showSignatureModal && createPortal(
        <div className="txn-modal-overlay" onClick={handleCloseSignatureModal}>
          <div className="signature-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signature-modal-header">
              <div className="signature-modal-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"/><polygon points="18 2 22 6 12 16 8 16 8 12 18 2"/></svg>
              </div>
              <h2 className="signature-modal-title">Send for Signature</h2>
              <p className="signature-modal-subtitle">
                Send "<strong>{formName}</strong>" to the following recipients:
              </p>
            </div>

            <div className="signature-modal-body">
              <div className="signature-section-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                <span>Recipients ({selectedParticipantIds.size} selected of {signatureParticipants.length})</span>
              </div>

              <div className="signature-participants-list">
                {isLoadingParticipants ? (
                  <div className="signature-loading">
                    <div className="signature-loading-spinner"></div>
                    <span>Loading participants...</span>
                  </div>
                ) : signatureParticipants.length === 0 ? (
                  <div className="signature-empty">
                    <div className="signature-empty-icon"><svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
                    <span>No participants found</span>
                    <p>Add participants to this transaction first</p>
                  </div>
                ) : (
                  signatureParticipants.map((participant, index) => {
                    const name = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || participant.name || '';
                    const email = participant.email || '';
                    const role = participant.role || participant.participantRole || participant.type || '';
                    const hasEmail = email && email.includes('@');
                    const participantKey = participant.id || email;
                    const isSelected = hasEmail && selectedParticipantIds.has(participantKey);
                    const initials = name
                      ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      : email ? email[0].toUpperCase() : '?';

                    return (
                      <div
                        key={participant.id || index}
                        className={`signature-participant-card${!hasEmail ? ' no-email' : ''}${isSelected ? ' selected' : ''}`}
                        onClick={() => hasEmail && handleToggleParticipant(participantKey)}
                        style={hasEmail ? { cursor: 'pointer' } : {}}
                      >
                        <div className="participant-avatar">{initials}</div>
                        <div className="participant-info">
                          {name && <div className="participant-name">{name}</div>}
                          {hasEmail
                            ? <div className="participant-email">{email}</div>
                            : <div className="participant-email no-email-warning">No email — add in Parties tab</div>
                          }
                          {role && <div className="participant-role">{role.replace(/_/g, ' ')}</div>}
                        </div>
                        <div className="participant-check">
                          {hasEmail
                            ? <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleParticipant(participantKey)}
                                onClick={e => e.stopPropagation()}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#007AFF' }}
                              />
                            : <span style={{ color: '#FF9500' }}>!</span>
                          }
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {sendError && <div className="lw-stage-form-error">{sendError}</div>}
            </div>

            <div className="signature-modal-footer">
              <button className="signature-cancel-btn" onClick={handleCloseSignatureModal} disabled={isSending}>
                Cancel
              </button>
              <button
                className="signature-send-btn"
                onClick={handleSend}
                disabled={isLoadingParticipants || selectedParticipantIds.size === 0 || isSending}
              >
                {isSending ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    <span>Send for Signature</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LoneWolfStageFormCard;
